import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import FeatureGate from '../components/FeatureGate';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';

const EXPENSE_CATEGORIES = [
  { value: 'helper_pay', label: 'Helper Pay' },
  { value: 'materials', label: 'Materials' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'repairs', label: 'Repairs' },
  { value: 'other', label: 'Other' },
];

const getCategoryLabel = (value) => {
  const cat = EXPENSE_CATEGORIES.find(c => c.value === value);
  return cat ? cat.label : value;
};

const formatPaymentMethod = (method) => {
  if (!method) return 'Not Specified';
  const methodMap = {
    'venmo': 'Venmo',
    'square': 'Square',
    'cash': 'Cash',
    'cashapp': 'CashApp',
    'check': 'Check',
    'zelle': 'Zelle',
  };
  return methodMap[method.toLowerCase()] || method.charAt(0).toUpperCase() + method.slice(1);
};

// Date range presets
const getDateRange = (preset) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  switch (preset) {
    case 'this_month':
      return {
        start: new Date(year, month, 1).toISOString().split('T')[0],
        end: new Date(year, month + 1, 0).toISOString().split('T')[0],
      };
    case 'last_month':
      return {
        start: new Date(year, month - 1, 1).toISOString().split('T')[0],
        end: new Date(year, month, 0).toISOString().split('T')[0],
      };
    case 'this_season':
      // Snow season is Oct - Apr
      // If we're Oct-Dec, season is Oct this year to Apr next year
      // If we're Jan-Apr, season is Oct last year to Apr this year
      // If we're May-Sep, season is Oct last year to Apr this year (off-season)
      let seasonStartYear, seasonEndYear;
      if (month >= 9) { // Oct (9) or later
        seasonStartYear = year;
        seasonEndYear = year + 1;
      } else { // Jan-Sep
        seasonStartYear = year - 1;
        seasonEndYear = year;
      }
      return {
        start: new Date(seasonStartYear, 9, 1).toISOString().split('T')[0], // Oct 1
        end: new Date(seasonEndYear, 3, 30).toISOString().split('T')[0], // Apr 30
      };
    case 'custom':
    default:
      return { start: '', end: '' };
  }
};

function ReportsContent() {
  const navigate = useNavigate();
  const { logout, isAdmin, isDriver, isSuperAdmin } = useAuth();
  const { getAppName } = useConfig();

  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Date range state
  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Report data
  const [revenueData, setRevenueData] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    byPaymentMethod: [],
  });
  const [expenseData, setExpenseData] = useState({
    total: 0,
    byCategory: [],
  });

  // Initialize dates on mount
  useEffect(() => {
    const range = getDateRange('this_month');
    setStartDate(range.start);
    setEndDate(range.end);
  }, []);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      params.append('product', PRODUCT_TYPE);

      const [revenueRes, expenseRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/reports/revenue?${params}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/expenses/summary?${params}`),
      ]);

      setRevenueData(revenueRes.data);
      setExpenseData({
        total: expenseRes.data.grandTotal || 0,
        byCategory: expenseRes.data.byCategory || [],
      });
    } catch (err) {
      console.error('Failed to load report data:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    if (startDate && endDate) {
      loadData();
    }
  }, [loadData, startDate, endDate]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Calculate profit metrics
  const totalRevenue = revenueData.total || 0;
  const totalExpenses = expenseData.total || 0;
  const netProfit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : 0;

  return (
    <div className="reports-page">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo">{getAppName()}</span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <span className="nav-link active">Reports</span>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin && isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <span className="mobile-nav-link active">Reports</span>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin && isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Profit & Loss Report</h1>
          <p className="text-muted">Financial overview for your business</p>
        </div>

        {/* Date Range Filters */}
        <div className="filters-section">
          <div className="preset-buttons">
            <button
              className={`preset-btn ${datePreset === 'this_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('this_month')}
            >
              This Month
            </button>
            <button
              className={`preset-btn ${datePreset === 'last_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('last_month')}
            >
              Last Month
            </button>
            <button
              className={`preset-btn ${datePreset === 'this_season' ? 'active' : ''}`}
              onClick={() => handlePresetChange('this_season')}
            >
              This Season
            </button>
            <button
              className={`preset-btn ${datePreset === 'custom' ? 'active' : ''}`}
              onClick={() => handlePresetChange('custom')}
            >
              Custom
            </button>
          </div>

          {datePreset === 'custom' && (
            <div className="custom-date-range">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Profit Summary Cards */}
            <div className="profit-summary">
              <div className="summary-card revenue">
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Total Revenue</div>
                  <div className="summary-value">{formatCurrency(totalRevenue)}</div>
                </div>
              </div>
              <div className="summary-card expenses">
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                    <path d="M1 10h22" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Total Expenses</div>
                  <div className="summary-value">{formatCurrency(totalExpenses)}</div>
                </div>
              </div>
              <div className={`summary-card profit ${netProfit >= 0 ? 'positive' : 'negative'}`}>
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Net Profit</div>
                  <div className="summary-value">{formatCurrency(netProfit)}</div>
                  <div className="summary-subtext">{profitMargin}% margin</div>
                </div>
              </div>
            </div>

            {/* Revenue Section */}
            <div className="report-section">
              <h2 className="section-title">Revenue Breakdown</h2>

              <div className="breakdown-cards">
                <div className="breakdown-card">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Payment Status</span>
                  </div>
                  <div className="breakdown-items">
                    <div className="breakdown-item">
                      <span className="item-label">
                        <span className="status-dot paid"></span>
                        Paid
                      </span>
                      <span className="item-value">{formatCurrency(revenueData.paid)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span className="item-label">
                        <span className="status-dot unpaid"></span>
                        Unpaid
                      </span>
                      <span className="item-value">{formatCurrency(revenueData.unpaid)}</span>
                    </div>
                  </div>
                </div>

                <div className="breakdown-card">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Payment Method</span>
                  </div>
                  <div className="breakdown-items">
                    {revenueData.byPaymentMethod && revenueData.byPaymentMethod.length > 0 ? (
                      revenueData.byPaymentMethod.map((method, idx) => (
                        <div key={idx} className="breakdown-item">
                          <span className="item-label">{formatPaymentMethod(method.payment_method)}</span>
                          <span className="item-value">{formatCurrency(method.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="breakdown-empty">No payment data</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Section */}
            <div className="report-section">
              <h2 className="section-title">Expense Breakdown</h2>

              <div className="breakdown-cards">
                <div className="breakdown-card full-width">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Category</span>
                    <span className="breakdown-total">{formatCurrency(totalExpenses)}</span>
                  </div>
                  <div className="breakdown-items">
                    {expenseData.byCategory && expenseData.byCategory.length > 0 ? (
                      expenseData.byCategory.map((cat, idx) => (
                        <div key={idx} className="breakdown-item">
                          <span className="item-label">
                            <span className={`category-badge ${cat.category}`}>
                              {getCategoryLabel(cat.category)}
                            </span>
                          </span>
                          <span className="item-value">{formatCurrency(cat.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="breakdown-empty">No expenses recorded</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .reports-page {
          min-height: 100vh;
          background: var(--color-background);
        }

        .page-header {
          margin-bottom: 24px;
        }

        .filters-section {
          margin-bottom: 24px;
        }

        .preset-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .preset-btn {
          padding: 10px 16px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          color: var(--color-text);
        }

        .preset-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        .preset-btn.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }

        .custom-date-range {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .date-input-group label {
          font-size: 0.85rem;
          color: var(--color-text-light);
        }

        .date-input-group .form-input {
          width: 160px;
        }

        /* Profit Summary Cards */
        .profit-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .summary-icon svg {
          width: 24px;
          height: 24px;
        }

        .summary-card.revenue .summary-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .summary-card.expenses .summary-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        .summary-card.profit .summary-icon {
          background: #dbeafe;
          color: #2563eb;
        }

        .summary-card.profit.positive .summary-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .summary-card.profit.negative .summary-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        .summary-content {
          flex: 1;
          min-width: 0;
        }

        .summary-label {
          font-size: 0.85rem;
          color: var(--color-text-light);
          margin-bottom: 4px;
        }

        .summary-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .summary-subtext {
          font-size: 0.8rem;
          color: var(--color-text-light);
          margin-top: 2px;
        }

        /* Report Sections */
        .report-section {
          margin-bottom: 32px;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 16px;
        }

        .breakdown-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .breakdown-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .breakdown-card.full-width {
          grid-column: 1 / -1;
        }

        .breakdown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }

        .breakdown-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }

        .breakdown-total {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-primary);
        }

        .breakdown-items {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .item-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: var(--color-text);
        }

        .item-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .status-dot.paid {
          background: #16a34a;
        }

        .status-dot.unpaid {
          background: #f59e0b;
        }

        .breakdown-empty {
          font-size: 0.9rem;
          color: var(--color-text-light);
          text-align: center;
          padding: 12px;
        }

        .category-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .category-badge.helper_pay { background: #dbeafe; color: #1e40af; }
        .category-badge.materials { background: #fef3c7; color: #92400e; }
        .category-badge.fuel { background: #fee2e2; color: #991b1b; }
        .category-badge.equipment { background: #e0e7ff; color: #3730a3; }
        .category-badge.repairs { background: #fce7f3; color: #9d174d; }
        .category-badge.other { background: #e5e7eb; color: #374151; }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .profit-summary {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .summary-card {
            padding: 16px;
          }

          .summary-value {
            font-size: 1.35rem;
          }

          .breakdown-cards {
            grid-template-columns: 1fr;
          }

          .preset-buttons {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .preset-btn {
            text-align: center;
          }

          .custom-date-range {
            flex-direction: column;
          }

          .date-input-group .form-input {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .summary-icon {
            width: 40px;
            height: 40px;
          }

          .summary-icon svg {
            width: 20px;
            height: 20px;
          }

          .summary-value {
            font-size: 1.25rem;
          }
        }

        /* Nav styles */
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }
      `}</style>
    </div>
  );
}

// Wrap the content in FeatureGate
function Reports() {
  return (
    <FeatureGate feature="reports">
      <ReportsContent />
    </FeatureGate>
  );
}

export default Reports;
